import { noop } from 'lodash';
import { all, call, fork, put, takeLatest } from 'redux-saga/effects';

import {
  hideActivationAccountModal,
  hideDeleteAccountModal,
  hideParentStaffModal,
  hideAddAccountModal,
  hideAddStudentForm,
  searchUsersFormSuccess,
  searchUsersFormError,
} from 'actions';
import { history } from 'store';
import { showErrorNotification, updateQuery } from 'actions/App';
import * as usersActions from 'actions/Users';
import * as profileActions from 'actions/Profile';
import * as students from 'actions/Students';
import * as usersApi from 'api/Users';
import * as profileApi from 'api/Profile';
import * as pupilsApi from 'api/Pupils';
import {
  GET_USER_BY_ID,
  DEACTIVATE_USER,
  ACTIVATE_USER,
  DELETE_USER_BY_ID,
  SEARCH_USERS,
  GET_STUDENT_INFO,
  CREATE_STUDENT,
  UPDATE_STUDENT,
  GET_SYSTEM_USER,
  UPDATE_SYSTEM_USER_ON_STUDENT,
  UPDATE_SYSTEM_USER_ON_CONTACT,
  CREATE_SYSTEM_USER_ON_STUDENT,
  CREATE_SYSTEM_USER_ON_CONTACT,
  SEARCH_USERS_FORM,
} from 'constants/ActionTypes';
import { USERS_QUERY_PARAMS } from 'constants/Values';

function* getUserById({ payload }) {
  try {
    const { data } = yield call(usersApi.getUserById, payload.id);
    yield put(usersActions.getUserByIdSuccess(data));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
    yield put(usersActions.clearUserById());
  }
}

function* deactivateUserRole({ payload }) {
  try {
    yield all(payload.ids.map(id => call(usersApi.deactivateUserRole, id)));
    yield put(usersActions.deactivateUserRoleSuccess());
    yield put(hideActivationAccountModal());
    yield put(usersActions.getUserById(payload.userId));
  } catch (error) {
    yield put(usersActions.deactivateUserRoleError());
    yield put(showErrorNotification(error.messages));
  }
}

function* activateUserRole({ payload }) {
  try {
    yield all(payload.ids.map(id => call(usersApi.activateUserRole, id)));
    yield put(usersActions.activateUserRoleSuccess());
    yield put(hideActivationAccountModal());
    yield put(usersActions.getUserById(payload.userId));
  } catch (error) {
    yield put(usersActions.activateUserRoleError());
    yield put(showErrorNotification(error.messages));
  }
}

function* deleteUser({ payload }) {
  try {
    yield call(usersApi.deleteUserById, payload.userId);
    yield put(hideDeleteAccountModal());
    history.push('/users/overview');
    yield put(usersActions.deleteUserSuccess());
  } catch (error) {
    yield put(usersActions.deleteUserError());
    yield put(showErrorNotification(error.messages));
  }
}

function* getStudentInfo({ payload }) {
  try {
    const { data } = yield call(usersApi.getSystemUser, payload.userId);
    yield put(usersActions.getStudentInfoSuccess({ data }));
  } catch (error) {
    yield put(usersActions.getStudentInfoError());
    yield put(showErrorNotification(error.messages));
  }
}

function* createStudent({ payload }) {
  try {
    const { data } = yield call(usersApi.createSystemUser, payload.formData);
    if (payload.avatar) {
      yield call(pupilsApi.uploadPupilAvatar, { pupilId: data.student.id, file: payload.avatar });
    }
    yield put(hideAddStudentForm());
    yield put(usersActions.createStudentSuccess());
  } catch (error) {
    yield put(usersActions.createStudentError());
    yield put(showErrorNotification(error.messages));
  }
}

function* updateStudent({ payload }) {
  try {
    yield call(usersApi.updateSystemUser, payload.id, payload.formData);
    if (payload.avatar) {
      yield call(pupilsApi.uploadPupilAvatar, { pupilId: payload.id, file: payload.avatar });
    }
    yield put(hideAddStudentForm());

    if (history.location.pathname === '/users/overview') {
      const query = window.location.href.split('?')[1];
      const obj = new URLSearchParams(query);
      const queryObj = {
        filter: obj.get('filter'),
        pagination: {
          pageNumber: obj.get('page') || 1,
          size: obj.get('rows') || 100,
        },
        role: obj.get('role'),
        accountFilter: obj.get('accountFilter') || 'ALL',
      };

      yield put(usersActions.searchUsers(queryObj));
    } else {
      yield put(usersActions.getUserById(payload.id));
    }

    yield put(usersActions.updateStudentSuccess());
  } catch (error) {
    yield put(usersActions.updateStudentError());
    yield put(showErrorNotification(error.messages));
  }
}

function* searchUsers({ payload }) {
  yield put(usersActions.setUsersLoading(true));

  try {
    const { data } = yield call(usersApi.searchUsers, payload.query);

    if (payload.affectQuery) {
      yield put(updateQuery(payload.query.pagination, USERS_QUERY_PARAMS, payload.query));
    }

    yield put(usersActions.searchUsersSuccess(data.entities, data.pageCount));
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  } finally {
    yield put(usersActions.setUsersLoading(false));
  }
}

function* searchUsersInForm({ payload }) {
  try {
    const { data } = yield call(usersApi.searchUsers, payload.query);
    yield put(searchUsersFormSuccess(data.entities, data.pageCount));
  } catch (error) {
    yield put(searchUsersFormError());
    yield put(showErrorNotification(error.messages));
  }
}

function* getSystemUser({ payload }) {
  const { userId, meta = noop, loading = noop } = payload;
  try {
    yield call(loading, true);
    const { data } = yield call(usersApi.getSystemUser, userId);
    yield call(meta, data);
    yield call(loading, false);
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  }
}

function* createSystemUserOnStudent({ payload }) {
  const { formData, meta = noop, pupilId } = payload;

  try {
    yield call(meta, true);

    yield call(usersApi.createSystemUser, formData);

    yield put(students.getStudentRelatives(pupilId));

    yield call(meta, false);

    yield put(hideParentStaffModal());
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  } finally {
    yield call(meta, false);
  }
}

function* createSystemUserOnContact({ payload }) {
  const { formData, meta = noop } = payload;

  try {
    yield call(meta, true);

    yield call(usersApi.createSystemUser, formData);

    const { data } = yield call(profileApi.getCurrentUser);
    yield put(profileActions.getCurrentUserSuccess(data));

    yield call(meta, false);

    yield put(hideParentStaffModal());
    yield put(hideAddAccountModal());
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  } finally {
    yield call(meta, false);
  }
}

function* updateSystemUserOnStudent({ payload }) {
  const { formData, meta = noop, userId, pupilId } = payload;

  try {
    yield call(meta, true);

    yield call(usersApi.updateSystemUser, userId, formData);

    if (history.location.pathname === '/users/overview') {
      const query = window.location.href.split('?')[1];
      const obj = new URLSearchParams(query);
      const queryObj = {
        filter: obj.get('filter'),
        pagination: {
          pageNumber: obj.get('page') || 1,
          size: obj.get('rows') || 100,
        },
        role: obj.get('role'),
        accountFilter: obj.get('accountFilter') || 'ALL',
      };
      yield put(usersActions.searchUsers(queryObj));
    } else {
      yield put(students.getStudentRelatives(pupilId));
    }

    yield call(meta, false);

    yield put(hideParentStaffModal());
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  } finally {
    yield call(meta, false);
  }
}

function* updateSystemUserOnContact({ payload }) {
  const { formData, meta = noop, userId } = payload;

  try {
    yield call(meta, true);

    const { data } = yield call(usersApi.updateSystemUser, userId, formData);
    const getRoleIds = () => {
      const roleIds = [];
      if (data.parent) {
        roleIds.push(data.parent.id);
      }
      if (data.staffMember) {
        roleIds.push(data.staffMember.id);
      }
      if (data.supervisor) {
        roleIds.push(data.supervisor.id);
      }
      return roleIds;
    };
    const roleIds = getRoleIds();
    const isOldId = roleIds.includes(userId);
    if (isOldId) {
      const response = yield call(usersApi.getUserById, userId);
      yield put(usersActions.getUserByIdSuccess(response.data));
      yield call(meta, false);
      yield put(hideParentStaffModal());
    } else {
      yield call(meta, false);
      yield put(hideParentStaffModal());
      history.push(`/users/${roleIds[0]}`);
    }
  } catch (error) {
    yield put(showErrorNotification(error.messages));
  } finally {
    yield call(meta, false);
  }
}

export function* getUserByIdSaga() {
  yield takeLatest(GET_USER_BY_ID, getUserById);
}

export function* deactivateUserRoleSaga() {
  yield takeLatest(DEACTIVATE_USER, deactivateUserRole);
}

export function* activateUserRoleSaga() {
  yield takeLatest(ACTIVATE_USER, activateUserRole);
}

export function* deleteUserSaga() {
  yield takeLatest(DELETE_USER_BY_ID, deleteUser);
}

export function* searchUsersSaga() {
  yield takeLatest(SEARCH_USERS, searchUsers);
}

export function* searchUsersInFormSaga() {
  yield takeLatest(SEARCH_USERS_FORM, searchUsersInForm);
}

export function* createSystemUserOnStudentSaga() {
  yield takeLatest(CREATE_SYSTEM_USER_ON_STUDENT, createSystemUserOnStudent);
}

export function* createSystemUserOnContactSaga() {
  yield takeLatest(CREATE_SYSTEM_USER_ON_CONTACT, createSystemUserOnContact);
}

export function* getStudentSaga() {
  yield takeLatest(GET_STUDENT_INFO, getStudentInfo);
}

export function* createStudentSaga() {
  yield takeLatest(CREATE_STUDENT, createStudent);
}

export function* updateStudentSaga() {
  yield takeLatest(UPDATE_STUDENT, updateStudent);
}

export function* getSystemUserSaga() {
  yield takeLatest(GET_SYSTEM_USER, getSystemUser);
}

export function* updateSystemUserOnStudentSaga() {
  yield takeLatest(UPDATE_SYSTEM_USER_ON_STUDENT, updateSystemUserOnStudent);
}

export function* updateSystemUserOnContactSaga() {
  yield takeLatest(UPDATE_SYSTEM_USER_ON_CONTACT, updateSystemUserOnContact);
}

export default function* rootSaga() {
  yield all([
    fork(getUserByIdSaga),
    fork(searchUsersSaga),
    fork(searchUsersInFormSaga),
    fork(deactivateUserRoleSaga),
    fork(activateUserRoleSaga),
    fork(deleteUserSaga),
    fork(createSystemUserOnStudentSaga),
    fork(createSystemUserOnContactSaga),
    fork(getStudentSaga),
    fork(createStudentSaga),
    fork(updateStudentSaga),
    fork(getSystemUserSaga),
    fork(updateSystemUserOnStudentSaga),
    fork(updateSystemUserOnContactSaga),
  ]);
}
